import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useStoreActions, useStoreState } from '~/store';
import useTranslation from '~/app/hooks/useTranslation';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import Loader from '~/app/common/Loader';
import AccessControl from '~/accessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { MessageType, EMPTY_COMMUNICATION_CHAT_DATA } from '~/app/data/statuses/chat';
import { route } from '~/app/helpers/route';
import { MACHINES_DETAILS_ROUTES } from '~/machines/Details/Router';
import { Machine, Project } from '~/types';
import Messages from '~/messages';
import classes from './style.module.scss';

export type CommunicationChatComponent = React.FC;

const CommunicationChat: CommunicationChatComponent = () => {
    const { t } = useTranslation('communication');
    const { setData } = useAppHeader();
    const { id, projectId } = useParams<{ id: Machine['id']; projectId: Project['id'] }>();
    const history = useHistory();
    const location = useLocation<{ chatId?: number; title?: string }>();
    const stateChatId = location.state?.chatId;
    const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

    const machine = useStoreState(state => state.machine?.data ?? ({} as Machine));
    const chatData = useStoreState(state => state.communicationChat?.data ?? EMPTY_COMMUNICATION_CHAT_DATA);
    const fetching = useStoreState(state => state.communicationChat?.fetching ?? false);
    const openChatAction = useStoreActions(actions => actions.communicationChat.openChat);
    const openChatByIdAction = useStoreActions(actions => actions.communicationChat.openChatById);
    const resetChatAction = useStoreActions(actions => actions.communicationChat.reset);
    const cancelPendingOpenAction = useStoreActions(actions => actions.communicationChat.cancelPendingOpen);
    const netWorkStatus = useStoreState(state => state.app.offline.status);

    const pageTitle = chatData.title || chatData.machineIntern || t('chatTitle') || '';
    const roleName = chatData.roleName;
    const machineIntern = chatData.machineIntern || machine.intern || '';
    const machineName = chatData.machineName || machine.name || '';
    const categoryImage = chatData.categoryImage || machine.categoryImage;
    const machineLine = `${machineIntern} ${machineName}`.trim().toUpperCase();
    const headerSubTitle = roleName
        ? `${machineLine} · ${roleName.toUpperCase()}`
        : machineLine;

    usePageTitle(pageTitle);

    useEffect(() => {
        if (stateChatId && !chatData.machineId && !fetching && netWorkStatus === 0 && !hasAttemptedFetch) {
            setHasAttemptedFetch(true);
            openChatByIdAction(stateChatId);
        }
    }, [stateChatId, chatData.machineId, fetching, netWorkStatus, openChatByIdAction, hasAttemptedFetch]);

    useEffect(() => {
        if (!fetching && !chatData.machineId) {
            if (!stateChatId || hasAttemptedFetch) {
                history.replace(projectId ? route(MACHINES_DETAILS_ROUTES.communication, { id, projectId }) : '/notifications/chat');
            }
        }
    }, [fetching, chatData.machineId, stateChatId, hasAttemptedFetch, history, id, projectId]);

    useEffect(() => {
        setData({
            title: pageTitle,
            subTitle: headerSubTitle,
            backLink: projectId ? route(MACHINES_DETAILS_ROUTES.details, { id, projectId }) : '/notifications/chat',
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: false,
            notification: false,
            search: false
        });
    }, [setData, pageTitle, headerSubTitle, id, projectId]);

    useEffect(() => {
        if (netWorkStatus !== 0 || fetching || !chatData.machineId || chatData.machineIntern) {
            return;
        }

        if (Number(chatData.id) > 0) {
            openChatByIdAction(Number(chatData.id));
            return;
        }

        const userIds = chatData.recipientUserIds;
        const roleId = chatData.roleId;
        if (!userIds?.length || !roleId) {
            return;
        }

        openChatAction({
            machineId: id,
            projectId,
            roleId,
            userIds,
            recipients: chatData.recipients,
        });
    }, [
        netWorkStatus,
        fetching,
        id,
        projectId,
        chatData.id,
        chatData.machineId,
        chatData.machineIntern,
        chatData.roleId,
        chatData.recipientUserIds,
        chatData.recipients,
        openChatAction,
        openChatByIdAction,
    ]);

    useEffect(() => () => {
        cancelPendingOpenAction();
        resetChatAction();
    }, [cancelPendingOpenAction, resetChatAction]);

    useEffect(() => {
        const main = document.getElementById('main-scrollable-wrapper');
        main?.classList.add('communication-chat-scroll-lock');
        return () => {
            main?.classList.remove('communication-chat-scroll-lock');
        };
    }, []);

    if (fetching && !chatData.machineId) {
        return <Loader size="32" />;
    }

    if (!chatData.machineId) {
        return (
            <AccessControl requiredPermissions={PERMISSION_GROUPS.machineRegistration}>
                <div className={classes['chat-page']} />
            </AccessControl>
        );
    }

    return (
        <AccessControl requiredPermissions={PERMISSION_GROUPS.machineRegistration}>
            <div className={classes['chat-page']}>
                {(machineIntern || machineName) ? (
                    <div className={classes['context-block']}>
                        <div className={classes['machine-row']}>
                            {categoryImage ? (
                                <img src={categoryImage} alt="" className={classes['category-image']} />
                            ) : null}
                            <div className={classes['machine-text']}>
                                {machineIntern ? (
                                    <div className={classes['machine-intern']}>{machineIntern}</div>
                                ) : null}
                                {machineName ? (
                                    <div className={classes['machine-name']}>{machineName}</div>
                                ) : null}
                            </div>
                        </div>
                        {roleName ? (
                            <div className={classes['info-row']}>
                                <div className={classes['field-label']}>{t('machineChatRole')}</div>
                                <div className={classes['field-value']}>{roleName}</div>
                            </div>
                        ) : null}
                        {chatData.categoryName ? (
                            <div className={classes['info-row']}>
                                <div className={classes['field-label']}>{t('machineChatCategory')}</div>
                                <div className={classes['field-value']}>{chatData.categoryName}</div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <div className={classes['messages-wrap']}>
                    <Messages
                        issue={chatData}
                        type={MessageType.MACHINE}
                        containerClassName={classes['messages-container']}
                        listClassName={classes['message-list']}
                    />
                </div>
            </div>
        </AccessControl>
    );
};

export default CommunicationChat;
